import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Instagram } from 'lucide-react';

const DEFAULT_LOGO = "https://customer-assets.emergentagent.com/job_0aec802e-e67b-4582-8fac-1517907b7262/artifacts/752tez4i_Logo%20Cucumaa%20Glass.png";

const DEFAULT_QUICK_LINKS = [
  { title: "Products", url: "/products" },
  { title: "Customize & Book", url: "/customize" },
  { title: "Industries", url: "/industries" },
  { title: "How It Works", url: "/how-it-works" },
  { title: "Wholesale Pricing", url: "/pricing" },
  { title: "Track Order", url: "/track" },
  { title: "Contact", url: "/contact" },
];

const Footer = () => {
  const [footer, setFooter] = useState(null);
  const [contact, setContact] = useState(null);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/erp/cms/public/footer`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) { setFooter(d.footer); setContact(d.contact); } })
      .catch(() => {});
  }, []);

  const phone = contact?.phone || '9284701985';
  const email = contact?.email_info || 'info@lucumaaglass.in';
  const address = contact?.address || 'Survey No 35/02, Yepre Patil Wearhousing, Dagde Vasti, Pisoli, Pune – 411060';
  const city = contact?.city || 'Pune';
  const state = contact?.state || 'Maharashtra';
  const pincode = contact?.pincode || '411060';
  const social = footer?.social_links || {};
  const quickLinks = footer?.quick_links?.length ? footer.quick_links : DEFAULT_QUICK_LINKS;
  const copyright = footer?.copyright_text || `© ${new Date().getFullYear()} Lucumaa Glass. A Unit of Lucumaa Corporation Pvt. Ltd. All rights reserved.`;
  const about = footer?.footer_about || 'Factory-direct toughened & customized glass solutions. Trusted by architects, builders, and homeowners across India.';
  const siteTitle = 'Lucumaa Glass';
  const siteTagline = 'Premium Glass Manufacturing';

  return (
    <footer className="bg-slate-900 text-slate-300" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <img 
                src={DEFAULT_LOGO} 
                alt={siteTitle} 
                className="h-12 w-auto object-contain bg-white rounded-lg p-1"
              />
              <div>
                <div className="text-lg font-bold text-white">{siteTitle}</div>
                <div className="text-xs text-slate-400">{siteTagline}</div>
              </div>
            </div>
            <p className="text-sm mb-6 leading-relaxed">
              {about}
            </p>
            <div className="flex gap-4">
              {social.facebook && (
                <a href={social.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-slate-800 hover:bg-primary-600 rounded-lg flex items-center justify-center transition-colors" data-testid="footer-facebook">
                  <Facebook className="w-5 h-5" />
                </a>
              )}
              {social.instagram && (
                <a href={social.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 bg-slate-800 hover:bg-primary-600 rounded-lg flex items-center justify-center transition-colors" data-testid="footer-instagram">
                  <Instagram className="w-5 h-5" />
                </a>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-4">Quick Links</h3>
            <ul className="space-y-3 text-sm">
              {quickLinks.map((link, i) => (
                <li key={i}>
                  {link.url.startsWith('http') ? (
                    <a href={link.url} target="_blank" rel="noopener noreferrer" className="hover:text-primary-400 transition-colors">{link.title}</a>
                  ) : (
                    <Link to={link.url} className="hover:text-primary-400 transition-colors">{link.title}</Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-4">Manufacturing Unit</h3>
            <div className="space-y-3 text-sm">
              <div className="flex gap-3">
                <MapPin className="w-5 h-5 text-primary-500 flex-shrink-0" />
                <p className="leading-relaxed">
                  {address}
                </p>
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-white font-semibold mb-4">Corporate Office</h3>
            <div className="space-y-3 text-sm">
              <div className="flex gap-3">
                <MapPin className="w-5 h-5 text-primary-500 flex-shrink-0" />
                <p className="leading-relaxed">
                  Shop No. 7 & 8, D Wing,<br />
                  Dynamic Grandeura,<br />
                  Undri, Pune – 411060
                </p>
              </div>
              <div className="flex gap-3">
                <Phone className="w-5 h-5 text-primary-500" />
                <a href={`tel:+91${phone}`} className="hover:text-primary-400 transition-colors">+91 {phone}</a>
              </div>
              <div className="flex gap-3">
                <Mail className="w-5 h-5 text-primary-500" />
                <a href={`mailto:${email}`} className="hover:text-primary-400 transition-colors">{email}</a>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-slate-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4 text-sm">
          <p>{copyright}</p>
          <div className="flex gap-6">
            <Link to="/privacy-policy" className="hover:text-primary-400 transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-primary-400 transition-colors">Terms of Service</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
